import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Autocomplete,
  Button,
  TextField,
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel
} from '@mui/material';
import DatePicker from '../common/DatePicker/DatePicker';
import { FilterFieldSx, TextFeaildSx, DropDownSX, CSS_PRIMARY } from '../../../constants/style';
import { Save, CirclePlus, Trash, UserPen, FilePlusCorner } from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import CommonDialog from '../common/CommonDialog';
import { MastersId } from '../../../constants/constant';
import {
  saveInstallation,
  getInstallationById,
  deleteInstallation
} from '../../../apis/InstallationDetails';
import { fetchGlobalTypesCombo } from '../../../apis/Masters/GlobalType';
import { fetchClients, fetchClientsById } from '../../../apis/Clients';
import Tooltip from '../../../utils/Tooltip';
import ChooseClient from '../common/ChooseClient';
import AmcList from './AmcList';
import AddAmc from '../AMC/AddAmc';
import { NumericFormat } from 'react-number-format';
import { userScreenAccess, formatDate } from '../../../utils';
import { fetchUsersCombo } from '../../../apis/Masters/Users';
import ConfirmDialog from '../common/ConfirmDialog';
import { DropDownSXWithColor } from '../../../constants/style';

const initialFormValues = {
  installationGUID: 0,
  clientGUID: 0,
  clientName: '',
  contactPersonName1: '',
  contactPersonName2: '',
  contactPersonName3: '',
  contactPersonName4: '',
  contactPersonName5: '',
  contactPersonName6: '',
  contactPersonDesignation1: '',
  contactPersonDesignation2: '',
  contactPersonDesignation3: '',
  contactPersonDesignation4: '',
  contactPersonDesignation5: '',
  contactPersonDesignation6: '',
  contactNo1: '',
  contactNo2: '',
  contactNo3: '',
  contactNo4: '',
  contactNo5: '',
  contactNo6: '',
  address1: '',
  address2: '',
  area: '',
  city: '',
  installationID: '',
  modules: '',
  installationDate: dayjs(),
  installationDateString: '',
  billAmount: 0,
  amcAmount: 0,
  userGUID: 0,
  createdGUID: 0,
  createdDate: '2026-04-21',
  modifiedGUID: 0,
  modifiedDate: '2026-04-21',
  versionNo: '',
  modulesList: [],
  isActive: true,
  address: '',
  contact1: '',
  contact2: '',
  contact3: '',
  contact4: '',
  contact5: '',
  contact6: '',
  installationDetails: {},
  emailID1: '',
  emailID2: '',
  tempAmcEndDate: null,
  amcEndDate: null
};

const AddInstallation = ({
  userDetails,
  clientsOptions,
  installation,
  inChargeOptions = [],
  userAccess,
  onSuccess,
  onCancel
}) => {
  const isEditMode = Boolean(installation);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [saving, setSaving] = useState(false);
  // Dialog state
  const [modulesTypes, setModulesTypes] = useState([]);
  const [clients, setClients] = useState(clientsOptions || []);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientDetailsLoading, setClientDetailsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [deleting, setDeleting] = useState(false);
  const [localInChargeOptions, setLocalInChargeOptions] = useState(inChargeOptions);
  const [amcList, setAmcList] = useState([]);
  const [openAddAmcDialog, setOpenAddAmcDialog] = useState(false);
  // const user = userDetails ? userDetails : JSON.parse(localStorage.getItem(localStorageKeyName));
  // const isUser = user.role == AuthConstant.USER;
  const isDeleteAllowd = isEditMode ? userAccess?.hasDeletePermission : false;
  const isReadonly = isEditMode ? !userAccess?.hasEditPermission : !userAccess?.hasAddPermission;
  const isClientPermission = userScreenAccess('/clients');
  const isAmcPermission = userScreenAccess('/amc-list');

  // ── Confirm-dialog state ────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: null
  });

  const openConfirm = ({ title, content, onConfirm }) =>
    setConfirmDialog({ open: true, title, content, onConfirm });

  const closeConfirm = () =>
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));

  const deleteHandler = () => {
    if (!userAccess?.hasDeletePermission) return;
    openConfirm({
      title: 'Delete Installation Record',
      content: `Are you sure you want to delete the Installation Record? This action cannot be undone.`,
      onConfirm: () => {
        closeConfirm();
        handleDelete();
      }
    });
  };

  const validate = (values) => {
    const errs = {};
    if (!values.installationDate) errs.installationDate = 'Date is required';
    if (!values.clientName.trim()) errs.clientName = 'Client Name is required';
    if (!values.billAmount) errs.billAmount = 'Bill Amount is required';
    if (!values.amcAmount) errs.amcAmount = 'AMC Amount is required';
    if (!values.versionNo) errs.versionNo = 'Version No is required';
    if (!values.userGUID) errs.inCharge = 'In Charge is required';
    return errs;
  };

  const handleCloseAddAmcDialog = () => {
    setOpenAddAmcDialog(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const successMsg = `Installation deleted successfully`;
      const response = await deleteInstallation(formValues?.installationGUID, successMsg, '');
      if (response && response.success) {
        onSuccess?.();
      }
    } catch (err) {
      console.log('Failed to delete global type:', err);
    } finally {
      setDeleting(false);
    }
  };

  const loadInstallationData = async () => {
    try {
      const installationData = await getInstallationById(installation.installationGUID);
      if (installationData) {
        const clientData = await fetchClientDetails(installationData.clientGUID);
        setAmcList(installationData.amcDetails || []);
        setFormValues({
          installationDate:
            installationData.installationDate && dayjs(installationData.installationDate).isValid()
              ? dayjs(installationData.installationDate).toDate()
              : null,
          installationGUID: installationData.installationGUID || 0,
          clientGUID: installationData.clientGUID || 0,
          clientName: installationData.clientName || '',
          contactPersonName1: installationData.contactPersonName1 || '',
          contactPersonName2: installationData.contactPersonName2 || '',
          contactPersonName3: installationData.contactPersonName3 || '',
          contactPersonName4: installationData.contactPersonName4 || '',
          contactPersonName5: installationData.contactPersonName5 || '',
          contactPersonName6: installationData.contactPersonName6 || '',
          contactPersonDesignation1: installationData.contactPersonDesignation1 || '',
          contactPersonDesignation2: installationData.contactPersonDesignation2 || '',
          contactPersonDesignation3: installationData.contactPersonDesignation3 || '',
          contactPersonDesignation4: installationData.contactPersonDesignation4 || '',
          contactPersonDesignation5: installationData.contactPersonDesignation5 || '',
          contactPersonDesignation6: installationData.contactPersonDesignation6 || '',
          contactNo1: installationData.contactNo1 || '',
          contactNo2: installationData.contactNo2 || '',
          contactNo3: installationData.contactNo3 || '',
          contactNo4: installationData.contactNo4 || '',
          contactNo5: installationData.contactNo5 || '',
          contactNo6: installationData.contactNo6 || '',
          emailID1: clientData.emailID1 || '',
          emailID2: clientData.emailID2 || '',
          address1: installationData.address1 || '',
          address2: installationData.address2 || '',
          area: installationData.area || '',
          city: installationData.city || '',
          installationID: installationData.installationID || '',
          installationDateString: installationData.installationDateString || '',
          modules: installationData.modules || '',
          billAmount: installationData.billAmount || 0,
          amcAmount: installationData.amcAmount || 0,
          userGUID: installationData.userGUID || 0,
          versionNo: installationData.versionNo || '',
          modulesList: installationData.modulesList || [],
          installationDetails: installationData?.installationDetails || {},
          tempAmcEndDate: installationData.tempAmcEndDate || null,
          amcEndDate: installationData.amcEndDate || null,
          address: clientData
            ? clientData.address1 +
              ', ' +
              clientData.address2 +
              ' ' +
              ', ' +
              clientData.area +
              ', ' +
              clientData.city
            : '',
          isActive: installationData.isActive,
          contact1: clientData
            ? [
                clientData.contactPersonName1,
                clientData.contactPersonDesignation1,
                clientData.contactNo1
              ]
                .filter(Boolean)
                .join(' | ')
            : '',

          contact2: clientData
            ? [
                clientData.contactPersonName2,
                clientData.contactPersonDesignation2,
                clientData.contactNo2
              ]
                .filter(Boolean)
                .join(' | ')
            : '',

          contact3: clientData
            ? [
                clientData.contactPersonName3,
                clientData.contactPersonDesignation3,
                clientData.contactNo3
              ]
                .filter(Boolean)
                .join(' | ')
            : '',

          contact4: clientData
            ? [
                clientData.contactPersonName4,
                clientData.contactPersonDesignation4,
                clientData.contactNo4
              ]
                .filter(Boolean)
                .join(' | ')
            : '',
          contact5: clientData
            ? [
                clientData.contactPersonName5,
                clientData.contactPersonDesignation5,
                clientData.contactNo5
              ]
                .filter(Boolean)
                .join(' | ')
            : '',
          contact6: clientData
            ? [
                clientData.contactPersonName6,
                clientData.contactPersonDesignation6,
                clientData.contactNo6
              ]
                .filter(Boolean)
                .join(' | ')
            : ''
        });
      }
    } catch (err) {
      console.log('Error loading installation data:', err);
    }
  };

  useEffect(() => {
    if (installation) {
      loadInstallationData();
    }
  }, [installation]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormValues((prev) => ({
      ...prev,
      [field]: value
    }));
  };
  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(formValues);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  const handleSave = async () => {
    const allTouched = {
      date: true,
      clientName: true,
      billAmount: true,
      amcAmount: true
    };
    setTouched(allTouched);
    const errs = validate(formValues);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const payload = {
      ...formValues,
      installationDate: formatDate(formValues.installationDate)
    };

    setSaving(true);
    const successMsg = installation
      ? 'Installation updated successfully'
      : 'Installation added successfully';
    const result = formValues.modulesList.join(';');

    const response = await saveInstallation(payload, result, successMsg, '');
    if (response.success) {
      setFormValues({
        ...initialFormValues
      });
      onSuccess?.(formValues);
    }
    setSaving(false);
  };

  const handleDateRangeChange = (nextRange) => {
    setFormValues({
      ...formValues,
      installationDate: nextRange || null
    });
  };

  const fetchDependencies = async () => {
    try {
      const modules = await fetchGlobalTypesCombo(MastersId.MODULES);
      setModulesTypes(modules || []);
    } catch (err) {
      console.log('Failed to fetch dependencies:', err);
    }
  };

  const fetchInChargeOptions = async () => {
    try {
      const userList = await fetchUsersCombo();
      setLocalInChargeOptions(userList);
    } catch (err) {
      console.log('Failed to fetch in charge options:', err);
    }
  };

  useEffect(() => {
    fetchInChargeOptions();
  }, []);

  const handleInChargeChange = (event, value) => {
    const userGUID = value?.userGUID || 0;
    const next = { ...formValues, userGUID };
    setFormValues(next);
  };

  const updateClientDetails = async (clientGUID) => {
    const clientData = await fetchClientDetails(clientGUID);
    const next = {
      ...formValues,
      clientName: clientData?.clientName || '',
      clientGUID: clientGUID || 0,
      userGUID: clientData?.userGUID || 0,
      address: clientData
        ? clientData.address1 +
          ', ' +
          clientData.address2 +
          ' ' +
          ', ' +
          clientData.area +
          ', ' +
          clientData.city
        : '',
      address1: clientData?.address1 || '',
      address2: clientData?.address2 || '',
      area: clientData?.area || '',
      city: clientData?.city || '',
      contactPersonName1: clientData?.contactPersonName1 || '',
      contactPersonDesignation1: clientData?.contactPersonDesignation1 || '',
      contactNo1: clientData?.contactNo1 || '',
      contactPersonName2: clientData?.contactPersonName2 || '',
      contactPersonDesignation2: clientData?.contactPersonDesignation2 || '',
      contactNo2: clientData?.contactNo2 || '',
      contactPersonName3: clientData?.contactPersonName3 || '',
      contactPersonDesignation3: clientData?.contactPersonDesignation3 || '',
      contactNo3: clientData?.contactNo3 || '',
      contactPersonName4: clientData?.contactPersonName4 || '',
      contactPersonDesignation4: clientData?.contactPersonDesignation4 || '',
      contactNo4: clientData?.contactNo4 || '',
      contactPersonName5: clientData?.contactPersonName5 || '',
      contactPersonDesignation5: clientData?.contactPersonDesignation5 || '',
      contactNo5: clientData?.contactNo5 || '',
      contactPersonName6: clientData?.contactPersonName6 || '',
      contactPersonDesignation6: clientData?.contactPersonDesignation6 || '',
      contactNo6: clientData?.contactNo6 || '',
      emailID1: clientData.emailID1 || '',
      emailID2: clientData.emailID2 || '',
      contact1: clientData
        ? [
            clientData.contactPersonName1,
            clientData.contactPersonDesignation1,
            clientData.contactNo1
          ]
            .filter(Boolean)
            .join(' | ')
        : '',

      contact2: clientData
        ? [
            clientData.contactPersonName2,
            clientData.contactPersonDesignation2,
            clientData.contactNo2
          ]
            .filter(Boolean)
            .join(' | ')
        : '',

      contact3: clientData
        ? [
            clientData.contactPersonName3,
            clientData.contactPersonDesignation3,
            clientData.contactNo3
          ]
            .filter(Boolean)
            .join(' | ')
        : '',

      contact4: clientData
        ? [
            clientData.contactPersonName4,
            clientData.contactPersonDesignation4,
            clientData.contactNo4
          ]
            .filter(Boolean)
            .join(' | ')
        : '',
      contact5: clientData
        ? [
            clientData.contactPersonName5,
            clientData.contactPersonDesignation5,
            clientData.contactNo5
          ]
            .filter(Boolean)
            .join(' | ')
        : '',
      contact6: clientData
        ? [
            clientData.contactPersonName6,
            clientData.contactPersonDesignation6,
            clientData.contactNo6
          ]
            .filter(Boolean)
            .join(' | ')
        : ''
    };
    setFormValues(next);
  };

  const handleClientChange = async (clientGUID) => {
    updateClientDetails(clientGUID);
  };

  const fetchClientListResponse = async (filters) => {
    setClientLoading(true);
    const payload = { ...filters, statusGUID: 1 };
    try {
      const response = await fetchClients(payload);
      setClients(response || []);
    } catch (err) {
      console.log('Error fetching client list:', err);
    } finally {
      setClientLoading(false);
    }
  };

  const editClientDetails = async () => {
    if (!formValues.clientGUID) return null;
    updateClientDetails(formValues.clientGUID);
  };

  const fetchClientDetails = async (clientGUID) => {
    if (!clientGUID) return null;
    setClientDetailsLoading(true);
    try {
      const response = await fetchClientsById(clientGUID);
      return response;
    } catch (err) {
      console.log('Error fetching client details:', err);
      return null;
    } finally {
      setClientDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchDependencies();
    fetchInChargeOptions();
    if (clients.length === 0) {
      fetchClientListResponse();
    }
    // if (isUser && !isEditMode) {
    //   setFormValues((prev) => ({
    //     ...prev,
    //     userGUID: userDetails.userGUID
    //   }));
    // }
  }, []);

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {clientDetailsLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: 1
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            opacity: clientDetailsLoading ? 0.5 : 1,
            pointerEvents: clientDetailsLoading ? 'none' : 'auto',
            alignItems: 'center'
          }}
        >
          {isEditMode ? (
            // EDIT MODE: Full-screen spacious layout
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                height: 'calc(100vh - 100px)',
                overflowX: 'auto',
                overflowY: 'auto',
                scrollBehavior: 'smooth',
                minWidth: 0
              }}
            >
              {/* Form Fields Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                {/* Row 1 (8-col): InstallDate(2) | ClientName+EditIcon(4) | InCharge(2) */}
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: { xs: '1fr', sm: '2fr 4fr 2fr' },
                    alignItems: 'center',
                    minWidth: 0,
                    mt: 1
                  }}
                >
                  <DatePicker
                    mode="single"
                    value={formValues.installationDate}
                    onChange={handleDateRangeChange}
                    height={36}
                    width="100%"
                    label="Installation Date"
                  />
                  <ChooseClient
                    clients={clients}
                    value={formValues.clientGUID}
                    onChange={handleClientChange}
                    clientLoading={clientLoading}
                    onBlur={handleBlur('clientName')}
                    error={touched.clientName && Boolean(errors.clientName)}
                    helperText={touched.clientName && errors.clientName}
                    isReadonly={isReadonly}
                    isClientPermission={isClientPermission}
                    isEditMode={isEditMode}
                    fetchClientListResponse={fetchClientListResponse}
                    editClientDetails={editClientDetails}
                  />
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={localInChargeOptions}
                    value={
                      localInChargeOptions.find((u) => u.userGUID === formValues.userGUID) || null
                    }
                    onChange={handleInChargeChange}
                    disablePortal={false}
                    getOptionLabel={(option) => option?.userName || ''}
                    isOptionEqualToValue={(option, value) => option?.userGUID === value?.userGUID}
                    readOnly={true}
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="In Charge"
                        placeholder="Select In Charge"
                        sx={FilterFieldSx}
                        onBlur={handleBlur('inCharge')}
                        error={touched.inCharge && Boolean(errors.inCharge)}
                        helperText={touched.inCharge && errors.inCharge}
                      />
                    )}
                  />
                </Box>

                {/* Row 2 (8-col): BillAmount(2) | AmcAmount(2) | Modules(4) */}
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 2fr 4fr' },
                    alignItems: 'flex-start',
                    minWidth: 0
                  }}
                >
                  <NumericFormat
                    value={formValues.billAmount}
                    onValueChange={(values) => {
                      handleChange('billAmount')({
                        target: {
                          name: 'billAmount',
                          value: values.floatValue || 0
                        }
                      });
                    }}
                    decimalScale={4}
                    customInput={TextField}
                    thousandSeparator
                    onBlur={handleBlur('billAmount')}
                    prefix="₹"
                    variant="outlined"
                    label="Bill Amount *"
                    fullWidth
                    size="small"
                    sx={FilterFieldSx}
                    disabled={isReadonly}
                  />
                  <NumericFormat
                    value={formValues.amcAmount}
                    onValueChange={(values) => {
                      handleChange('amcAmount')({
                        target: {
                          name: 'amcAmount',
                          value: values.floatValue || 0
                        }
                      });
                    }}
                    decimalScale={4}
                    customInput={TextField}
                    thousandSeparator
                    onBlur={handleBlur('amcAmount')}
                    prefix="₹"
                    variant="outlined"
                    label="AMC Amount *"
                    fullWidth
                    size="small"
                    sx={FilterFieldSx}
                    disabled={isReadonly}
                  />
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={modulesTypes}
                    value={modulesTypes.filter((m) =>
                      formValues.modulesList.includes(m.globalTypeID)
                    )}
                    sx={DropDownSXWithColor}
                    onChange={(_, selectedOptions) =>
                      setFormValues((prev) => ({
                        ...prev,
                        modulesList: selectedOptions.map((opt) => opt.globalTypeID)
                      }))
                    }
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.globalTypeID
                    }
                    readOnly={isReadonly}
                    renderOption={({ key, ...props }, option, { selected }) => (
                      <li key={key} {...props}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        {option.globalTypeName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select modules"
                        placeholder="Choose modules"
                        onBlur={handleBlur('modules')}
                        error={touched.modules && Boolean(errors.modules)}
                        helperText={touched.modules && errors.modules}
                      />
                    )}
                    size="small"
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                  />
                </Box>

                {/* Row 3: Address + Contact Details (with buttons) — evenly split */}
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: { xs: '1fr', md: '2fr 6fr' },
                    minWidth: 0,
                    mb: 1
                  }}
                >
                  {/* Address */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{
                        mb: 0.5
                      }}
                    >
                      Address
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={5}
                      value={formValues.address}
                      onChange={handleChange('address')}
                      onBlur={handleBlur('address')}
                      error={touched.address && Boolean(errors.address)}
                      helperText={touched.address && errors.address}
                      size="small"
                      slotProps={{ input: { readOnly: true } }}
                      sx={TextFeaildSx}
                    />
                  </Box>

                  {/* Contact Details + Action Buttons */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{
                        mb: 0.5
                      }}
                    >
                      Contact Details
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                        flex: 1
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Person 1"
                        multiline
                        minRows={1}
                        maxRows={3}
                        value={formValues.contact1}
                        onChange={handleChange('contact1')}
                        size="small"
                        slotProps={{ input: { readOnly: true } }}
                        sx={TextFeaildSx}
                      />
                      <TextField
                        fullWidth
                        label="Person 2"
                        multiline
                        minRows={1}
                        maxRows={3}
                        value={formValues.contact2}
                        onChange={handleChange('contact2')}
                        size="small"
                        slotProps={{ input: { readOnly: true } }}
                        sx={TextFeaildSx}
                      />
                      <TextField
                        fullWidth
                        label="Person 3"
                        multiline
                        minRows={1}
                        maxRows={3}
                        value={formValues.contact3}
                        onChange={handleChange('contact3')}
                        size="small"
                        slotProps={{ input: { readOnly: true } }}
                        sx={TextFeaildSx}
                      />
                      <TextField
                        fullWidth
                        label="Person 4"
                        multiline
                        minRows={1}
                        maxRows={3}
                        value={formValues.contact4}
                        onChange={handleChange('contact4')}
                        size="small"
                        slotProps={{ input: { readOnly: true } }}
                        sx={TextFeaildSx}
                      />
                      <TextField
                        fullWidth
                        label="Person 5"
                        multiline
                        minRows={1}
                        maxRows={3}
                        value={formValues.contact5}
                        onChange={handleChange('contact5')}
                        size="small"
                        slotProps={{ input: { readOnly: true } }}
                        sx={TextFeaildSx}
                      />
                      <TextField
                        fullWidth
                        label="Person 6"
                        multiline
                        minRows={1}
                        maxRows={3}
                        value={formValues.contact6}
                        onChange={handleChange('contact6')}
                        size="small"
                        slotProps={{
                          input: {
                            readOnly: true
                          }
                        }}
                        sx={TextFeaildSx}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Row 3 (8-col): VersionNo(2) — expandable for future Email fields */}
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: { xs: '1fr', sm: '2fr 3fr 3fr' },
                    alignItems: 'flex-start',
                    minWidth: 0
                  }}
                >
                  <TextField
                    fullWidth
                    label="Version No"
                    value={formValues.versionNo}
                    onChange={handleChange('versionNo')}
                    onBlur={handleBlur('versionNo')}
                    error={touched.versionNo && Boolean(errors.versionNo)}
                    helperText={touched.versionNo && errors.versionNo}
                    sx={FilterFieldSx}
                    slotProps={{
                      input: {
                        readOnly: isReadonly
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Email ID 1"
                    value={formValues.emailID1}
                    onChange={handleChange('emailID1')}
                    onBlur={handleBlur('emailID1')}
                    error={touched.emailID1 && Boolean(errors.emailID1)}
                    helperText={touched.emailID1 && errors.emailID1}
                    sx={FilterFieldSx}
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Email ID 2"
                    value={formValues.emailID2}
                    onChange={handleChange('emailID2')}
                    onBlur={handleBlur('emailID2')}
                    error={touched.emailID2 && Boolean(errors.emailID2)}
                    helperText={touched.emailID2 && errors.emailID2}
                    sx={FilterFieldSx}
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  flex: '1 1 50%',
                  minWidth: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.isActive}
                      onChange={handleChange('isActive')}
                      size="small"
                      sx={{
                        color: 'primary.main',
                        '&.Mui-checked': { color: CSS_PRIMARY }
                      }}
                      readOnly={isReadonly}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Active
                    </Typography>
                  }
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'center',
                  mt: 2,
                  pt: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
                  }
                  disabled={deleting}
                  onClick={deleteHandler}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 100,
                    background: '#e05757',
                    '&:hover': { background: '#c73e3e' },
                    boxShadow: '0 4px 12px rgba(224, 87, 87, 0.3)',
                    display: !isEditMode || !isDeleteAllowd ? 'none' : 'inline-flex'
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />
                  }
                  onClick={handleSave}
                  disabled={saving || Object.keys(validate(formValues)).length > 0}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 100,
                    background: 'var(--primary, #36c95f)',
                    '&:hover': { background: 'var(--primary-dark, #27ae60)' },
                    boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
                    display: isReadonly ? 'none' : 'inline-flex'
                  }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FilePlusCorner size={14} />}
                  onClick={() => {
                    setOpenAddAmcDialog(true);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 100,
                    background: '#337ab7',
                    '&:hover': { background: '#286090' },
                    boxShadow: '0 4px 12px rgba(51,122,183,0.3)',
                    display: isAmcPermission?.hasAddPermission ? 'inline-flex' : 'none'
                  }}
                >
                  Add AMC
                </Button>
              </Box>

              {/* AMC List Table — expanded view */}
              <Box sx={{ flex: 2, minHeight: '400px', overflow: 'auto' }}>
                <AmcList
                  isUser={isAmcPermission?.hasEditPermission}
                  installationGUID={formValues?.installationGUID}
                  amcList={amcList}
                  onRefresh={loadInstallationData}
                />
              </Box>
            </Box>
          ) : (
            // ADD MODE: Keep existing sequential layout
            <>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1,
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: '1fr'
                  }
                }}
              >
                <Box sx={{ display: 'grid', gap: 1 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: '1fr 1.5fr 1fr'
                      }
                    }}
                  >
                    <DatePicker
                      mode="single"
                      value={formValues.installationDate}
                      onChange={handleDateRangeChange}
                      height={36}
                      width="100%"
                      label="Installation Date"
                    />
                    <ChooseClient
                      clients={clients}
                      value={formValues.clientGUID}
                      onChange={handleClientChange}
                      clientLoading={clientLoading}
                      onBlur={handleBlur('clientName')}
                      error={touched.clientName && Boolean(errors.clientName)}
                      helperText={touched.clientName && errors.clientName}
                      isReadonly={isReadonly}
                      isClientPermission={isClientPermission}
                      isEditMode={false}
                      fetchClientListResponse={fetchClientListResponse}
                      editClientDetails={editClientDetails}
                    />
                    <NumericFormat
                      value={formValues.billAmount}
                      onValueChange={(values) => {
                        handleChange('billAmount')({
                          target: {
                            name: 'billAmount',
                            value: values.floatValue || 0
                          }
                        });
                      }}
                      decimalScale={4}
                      customInput={TextField}
                      thousandSeparator
                      onBlur={handleBlur('billAmount')}
                      error={touched.billAmount && Boolean(errors.billAmount)}
                      prefix="₹"
                      variant="outlined"
                      label="Bill Amount *"
                      fullWidth
                      size="small"
                      sx={FilterFieldSx}
                      disabled={isReadonly}
                    />
                    <NumericFormat
                      value={formValues.amcAmount}
                      onValueChange={(values) => {
                        handleChange('amcAmount')({
                          target: {
                            name: 'amcAmount',
                            value: values.floatValue || 0
                          }
                        });
                      }}
                      decimalScale={4}
                      customInput={TextField}
                      thousandSeparator
                      onBlur={handleBlur('amcAmount')}
                      prefix="₹"
                      variant="outlined"
                      label="AMC Amount *"
                      fullWidth
                      size="small"
                      sx={FilterFieldSx}
                      disabled={isReadonly}
                      error={touched.amcAmount && Boolean(errors.amcAmount)}
                    />
                    <TextField
                      fullWidth
                      label="Version No *"
                      value={formValues.versionNo}
                      onChange={handleChange('versionNo')}
                      onBlur={handleBlur('versionNo')}
                      error={touched.versionNo && Boolean(errors.versionNo)}
                      helperText={touched.versionNo && errors.versionNo}
                      sx={FilterFieldSx}
                      inputProps={{ readOnly: isReadonly }}
                    />
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={localInChargeOptions}
                      value={
                        localInChargeOptions.find((u) => u.userGUID === formValues.userGUID) || null
                      }
                      onChange={handleInChargeChange}
                      disablePortal={false}
                      getOptionLabel={(option) => option?.userName || ''}
                      isOptionEqualToValue={(option, value) => option?.userGUID === value?.userGUID}
                      readOnly={true}
                      slotProps={{
                        paper: {
                          sx: DropDownSX
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="In Charge"
                          placeholder="Select In Charge"
                          sx={FilterFieldSx}
                          onBlur={handleBlur('inCharge')}
                          error={touched.inCharge && Boolean(errors.inCharge)}
                          helperText={touched.inCharge && errors.inCharge}
                        />
                      )}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      gridTemplateColumns: { xs: '1fr', sm: '4fr 4fr' },
                      alignItems: 'flex-start',
                      minWidth: 0
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Email ID 1"
                      value={formValues.emailID1}
                      onChange={handleChange('emailID1')}
                      onBlur={handleBlur('emailID1')}
                      error={touched.emailID1 && Boolean(errors.emailID1)}
                      helperText={touched.emailID1 && errors.emailID1}
                      sx={FilterFieldSx}
                      slotProps={{
                        input: {
                          readOnly: true
                        }
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Email ID 2"
                      value={formValues.emailID2}
                      onChange={handleChange('emailID2')}
                      onBlur={handleBlur('emailID2')}
                      error={touched.emailID2 && Boolean(errors.emailID2)}
                      helperText={touched.emailID2 && errors.emailID2}
                      sx={FilterFieldSx}
                      slotProps={{
                        input: {
                          readOnly: true
                        }
                      }}
                    />
                  </Box>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={modulesTypes}
                    readOnly={isReadonly}
                    value={modulesTypes.filter((m) =>
                      formValues.modulesList.includes(m.globalTypeID)
                    )}
                    onChange={(_, selectedOptions) =>
                      setFormValues((prev) => ({
                        ...prev,
                        modulesList: selectedOptions.map((opt) => opt.globalTypeID)
                      }))
                    }
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.globalTypeID
                    }
                    renderOption={({ key, ...props }, option, { selected }) => (
                      <li key={key} {...props}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        {option.globalTypeName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select modules *"
                        placeholder="Choose modules"
                        onBlur={handleBlur('modules')}
                        error={touched.modules && Boolean(errors.modules)}
                        helperText={touched.modules && errors.modules}
                      />
                    )}
                    sx={{ flex: 1, ...DropDownSXWithColor }}
                    size="small"
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    minRows={1}
                    maxRows={8}
                    value={formValues.address}
                    onChange={handleChange('address')}
                    onBlur={handleBlur('address')}
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                    sx={TextFeaildSx}
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ my: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  Contact Details
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr 1fr 1fr'
                    }
                  }}
                >
                  <TextField
                    fullWidth
                    label="Person 1"
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={formValues.contact1}
                    onChange={handleChange('contact1')}
                    size="small"
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                    sx={TextFeaildSx}
                  />
                  <TextField
                    fullWidth
                    label="Person 2"
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={formValues.contact2}
                    size="small"
                    onChange={handleChange('contact2')}
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                    sx={TextFeaildSx}
                  />
                  <TextField
                    fullWidth
                    label="Person 3"
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={formValues.contact3}
                    size="small"
                    onChange={handleChange('contact3')}
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                    sx={TextFeaildSx}
                  />
                  <TextField
                    fullWidth
                    label="Person 4"
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={formValues.contact4}
                    size="small"
                    onChange={handleChange('contact4')}
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                    sx={TextFeaildSx}
                  />
                  <TextField
                    fullWidth
                    label="Person 5"
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={formValues.contact5}
                    size="small"
                    onChange={handleChange('contact5')}
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                    sx={TextFeaildSx}
                  />
                  <TextField
                    fullWidth
                    label="Person 6"
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={formValues.contact6}
                    size="small"
                    onChange={handleChange('contact6')}
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                    sx={TextFeaildSx}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  flex: '1 1 50%',
                  minWidth: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.isActive}
                      onChange={handleChange('isActive')}
                      size="small"
                      sx={{
                        color: 'primary.main',
                        '&.Mui-checked': { color: CSS_PRIMARY }
                      }}
                      readOnly={isReadonly}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Active
                    </Typography>
                  }
                />
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onCancel}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    color: 'text.secondary',
                    borderColor: '#d1d5db'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />
                  }
                  onClick={handleSave}
                  disabled={saving || Object.keys(validate(formValues)).length > 0}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 100,
                    background: 'var(--primary, #36c95f)',
                    '&:hover': { background: 'var(--primary-dark, #27ae60)' },
                    boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
                    display: isReadonly ? 'none' : 'inline-flex'
                  }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <CommonDialog
        open={openAddAmcDialog}
        onClose={handleCloseAddAmcDialog}
        title="Add AMC"
        persistent={true}
      >
        <AddAmc
          installationId={installation?.installationGUID}
          amcEndDate={formValues?.amcEndDate?.includes('0001') ? null : formValues?.amcEndDate}
          userAccess={isAmcPermission}
          tempAmcEndDate={
            formValues?.tempAmcEndDate?.includes('0001') ? null : formValues?.tempAmcEndDate
          }
          onSuccess={() => {
            handleCloseAddAmcDialog();
            loadInstallationData();
          }}
        />
      </CommonDialog>

      {/* ── Confirm Dialog ─────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        titleIcon={<Trash size={18} />}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </>
  );
};

export default AddInstallation;
